import React from "react";

import { faEllipsis } from "@fortawesome/pro-regular-svg-icons";
import { faGlobe } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
	Chip,
	DropDownMenu,
	Icon,
	SingleSelectInput,
	Skeleton,
	Table,
	Text,
	Tooltip,
} from "@performyard/py-components";
import type { PYColumnDef, SortChangedEvent } from "@performyard/py-components";
import type {
	CompetencyAssignmentJSON,
	CompetencyAssignmentUserDict,
	CompetencyDict,
} from "@performyard/service-contracts";

import type { SearchServiceSort } from "../../../../api/hooks";

import {
	NOT_ASSESSED_LABEL,
	levelSelectOptions,
	rankToSelectValue,
	resolveLevelName,
	selectValueToRank,
} from "../../utils";

import { CaretCell, CompetencySuperRowContent, isLoadingRow, useSuperRow } from "../SuperRow";
import type { CompetencyAssignmentRow, SuperRowContext } from "../SuperRow";
import type { PendingLevelEdits } from "../useBulkLevelEdit";

type CompetenciesTableProps = {
	assignments: CompetencyAssignmentJSON[];
	competencies: CompetencyDict;
	/** When supplied, splices Assignee + Status + Role columns after Competency (Team + Company layouts). */
	users?: CompetencyAssignmentUserDict;
	/** All Competencies (company) only — splices a Manager column after Role. */
	showManager?: boolean;
	loading: boolean;
	canUpdateCurrentLevel: boolean;
	sort: SearchServiceSort;
	// TData deliberately unpinned: the grid emits Row-typed events, pages annotate JSON-typed
	// handlers, and ag-grid event types are invariant in TData — any is the only common ground.
	onSortChanged: (e: SortChangedEvent) => void;
	onOpenPanel: (type: "history", row: CompetencyAssignmentJSON) => void;
	onUpdateLevel: (row: CompetencyAssignmentJSON) => void;
	/** Bulk edit mode (Update Current Levels) — swaps the Current Level column into selects. */
	editMode: boolean;
	/** Keyed by assignment `_id`. */
	pendingLevels: PendingLevelEdits;
	onLevelChange: (assignmentId: string, value: number | null, currentValue: number | null) => void;
	isSaving: boolean;
	/** At the per-request cap, freezes intake on rows not already pending (PY-14092). */
	atCap: boolean;
};

const CompetenciesTable = ({
	assignments,
	competencies,
	users,
	showManager,
	loading,
	canUpdateCurrentLevel,
	sort,
	onSortChanged,
	onOpenPanel,
	onUpdateLevel,
	editMode,
	pendingLevels,
	onLevelChange,
	isSaving,
	atCap,
}: CompetenciesTableProps): React.ReactElement => {
	const superRow = useSuperRow({
		rows: assignments,
		competencies,
		getRowId: (row) => row._id,
		getInitialExpandedRank: (row) => row.current_level,
	});

	const statusColumn: PYColumnDef<CompetencyAssignmentRow> = {
		colId: "status",
		headerName: "Status",
		// Not sortable — status isn't a supported AssignmentSortKey on the assignments list API.
		sortable: false,
		// Team/Company reclaim chip space for the busier layouts; My keeps the roomier column.
		width: users ? 88 : 100,
		cellStyle: { display: "flex", alignItems: "center" },
		cellRenderer: (params: { data: CompetencyAssignmentJSON }) => {
			const row = params.data;
			if (isLoadingRow(row)) return <Skeleton className="h-5 w-14 rounded-full" />;
			return row.status === "active" ? (
				<Chip label="Active" color="primary" size={20} />
			) : (
				// An archived ASSIGNMENT means the person is no longer assigned — "Archived"
				// wrongly reads as the competency itself being archived.
				<Chip label="Unassigned" color="neutral" size={20} />
			);
		},
	};

	// Status sits between Assignee and Role on the Team/Company layouts; with no user columns it
	// follows Competency directly.
	const userColumns: PYColumnDef<CompetencyAssignmentRow>[] = users
		? [
				{
					// Built-in employee column → AvatarProfile (avatar + linked name) + loading shimmer.
					colId: "assignee_name",
					headerName: "Assignee",
					type: "employee",
					flex: 1,
					sortable: true,
					valueGetter: (p) => ({
						_id: p.data?.user_id ?? "",
						name: p.data ? users[p.data.user_id]?.name : undefined,
					}),
				},
				statusColumn,
				{
					colId: "role_name",
					headerName: "Role",
					flex: 1,
					sortable: false,
					cellRenderer: (params: { data: CompetencyAssignmentJSON }) => {
						const row = params.data;
						if (isLoadingRow(row)) return <Skeleton className="h-4 w-1/2" />;
						const name = users[row.user_id]?.role_name ?? null;
						return name ? (
							<Text size="paragraph-3" weight="regular">
								<span className="py-line-clamp-1">{name}</span>
							</Text>
						) : null;
					},
				},
				...(showManager
					? [
							{
								colId: "manager_name",
								headerName: "Manager",
								flex: 1,
								sortable: false,
								cellRenderer: (params: { data: CompetencyAssignmentJSON }) => {
									const row = params.data;
									if (isLoadingRow(row)) return <Skeleton className="h-4 w-1/2" />;
									const name = users[row.user_id]?.manager_name ?? null;
									return name ? (
										<Text size="paragraph-3" weight="regular">
											<span className="py-line-clamp-1">{name}</span>
										</Text>
									) : null;
								},
							} as PYColumnDef<CompetencyAssignmentRow>,
						]
					: []),
			]
		: [statusColumn];

	const columns: PYColumnDef<CompetencyAssignmentRow>[] = [
		{
			colId: "super_row_caret",
			headerName: "",
			sortable: false,
			width: 40,
			resizable: false,
			suppressSizeToFit: true,
			// !p-0 kills the theme's cell padding — the caret's 16px left offset lives in CaretCell,
			// mirroring the kebab's 16px from the right edge.
			cellClass: "!p-0",
			cellStyle: { display: "flex", alignItems: "center" },
			cellRenderer: CaretCell,
		},
		{
			colId: "competency_name",
			headerName: "Competency",
			flex: 1,
			sortable: true,
			cellRenderer: (params: { data: CompetencyAssignmentRow; context?: SuperRowContext }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-3/4" />;
				const entry = competencies[row.competency_id];
				const isOpen = params.context?.expandedRowId === row._rowId;
				return (
					<div className="flex min-w-0 items-center gap-2">
						<Text
							size="paragraph-3"
							weight="regular"
							color={isOpen ? "primary-5" : undefined}
							dangerouslySetClassName="min-w-0"
						>
							<span className="block py-text-truncate">{entry?.name}</span>
						</Text>
						{entry?.is_company_wide && (
							<Tooltip content="Company Wide Competency">
								<span
									data-testid="company-wide-icon"
									className="flex size-4 shrink-0 items-center justify-center"
								>
									<FontAwesomeIcon icon={faGlobe} className="text-sm text-primary-5" />
								</span>
							</Tooltip>
						)}
					</div>
				);
			},
		},
		...userColumns,
		{
			colId: "current_level",
			headerName: "Current Level",
			sortable: true,
			// My Competencies has no user columns, so the level columns share the slack instead of
			// the name column swallowing it all. 184 hugs the 160px edit select (PY-14091).
			...(users ? { width: 184 } : { flex: 1, minWidth: 184 }),
			cellRenderer: (params: { data: CompetencyAssignmentJSON }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-20" />;
				const levels = competencies[row.competency_id]?.levels ?? [];
				// Mirrors the kebab's own gating (an archived assignment 400s the update backend-side) —
				// an editable select here would just be a guaranteed, unfixable-while-archived failure.
				if (editMode && levels.length > 0 && row.status !== "archived") {
					const pendingValue = row._id in pendingLevels ? pendingLevels[row._id] : row.current_level;
					const levelOptions = levelSelectOptions(levels, NOT_ASSESSED_LABEL);
					const selectedLabel = levelOptions.find(
						(option) => option.value === rankToSelectValue(pendingValue)
					)?.label;
					return (
						<div className="w-[160px]" title={selectedLabel}>
							<SingleSelectInput<number>
								value={rankToSelectValue(pendingValue)}
								options={levelOptions}
								onChange={(value) =>
									onLevelChange(row._id, selectValueToRank(value), row.current_level)
								}
								disabled={isSaving || (atCap && !(row._id in pendingLevels))}
								ariaLabel={`Current level for ${competencies[row.competency_id]?.name ?? "competency"}`}
							/>
						</div>
					);
				}
				const name = resolveLevelName(levels, row.current_level);
				return name ? (
					<Text size="paragraph-3" weight="regular">
						<span className="py-line-clamp-1">{name}</span>
					</Text>
				) : null;
			},
		},
		{
			colId: "expected_level",
			headerName: "Expected Level",
			sortable: true,
			...(users ? { width: 160 } : { flex: 1, minWidth: 160 }),
			cellRenderer: (params: { data: CompetencyAssignmentJSON }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-20" />;
				const name = resolveLevelName(competencies[row.competency_id]?.levels, row.expected_level);
				return name ? (
					<Text size="paragraph-3" weight="regular">
						<span className="py-line-clamp-1">{name}</span>
					</Text>
				) : null;
			},
		},
		{
			// Delta chip + kebab render as one right-aligned flex group so their spacing is plain
			// Tailwind (gap/pr) — the grid theme's own cell padding (which wins over inline
			// cellStyle padding on the last column) is neutralized once with !p-0.
			colId: "actions",
			headerName: "",
			sortable: false,
			width: users ? 88 : 96,
			resizable: false,
			suppressSizeToFit: true,
			cellClass: "!p-0",
			cellRenderer: (params: { data: CompetencyAssignmentJSON }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-8 rounded-full" />;
				const { current_level, expected_level } = row;
				const delta = current_level != null && expected_level != null ? current_level - expected_level : 0;
				// Archived rows can't be updated backend-side — offering the action here would just 400.
				const kebabOptions = [
					{ key: "view-history", label: "View History" },
					// Dropped in edit mode too: the bulk save would overwrite whatever a single-row write set.
					...(canUpdateCurrentLevel && row.status !== "archived" && !editMode
						? [{ key: "update-level", label: "Update Current Level" }]
						: []),
				];
				// pr-0.5: the ellipsis glyph carries ~14px of its own right inset — 2px lands the
				// last dot 16px from the table edge, per design.
				return (
					<div className="flex w-full items-center justify-end gap-4 pr-0.5">
						{delta !== 0 && (
							<Chip
								label={delta > 0 ? `+${delta}` : `${delta}`}
								color={delta > 0 ? "green" : "red"}
								size={20}
							/>
						)}
						<DropDownMenu
							options={kebabOptions}
							onSelect={(key) => {
								if (key === "view-history") onOpenPanel("history", row);
								else if (key === "update-level") onUpdateLevel(row);
							}}
							placement="bottom-end"
							minWidth={120}
						>
							{/* padding halves inside items-center — pt-4 nets the original 8px downward nudge */}
							<span className="flex items-center pt-4">
								<Icon icon={faEllipsis} size={24} color="neutral-5" />
							</span>
						</DropDownMenu>
					</div>
				);
			},
		},
	];

	return (
		// While the edit bar sits on top of the table, square the table's top corners so the two
		// read as one surface (PY-14103). The ! is load-bearing: ag's runtime-injected theme CSS is
		// unlayered and beats Tailwind v4's layered utilities at any specificity.
		<div className={`min-w-0 flex-1 ${editMode ? "[&_.ag-root-wrapper]:!rounded-t-none" : ""}`}>
			<Table<CompetencyAssignmentRow>
				ref={superRow.gridRef}
				autoHeight
				columnDefs={columns}
				rowData={superRow.rowData}
				loading={loading}
				getRowId={(params) => params.data._rowId}
				rowHeight={48}
				context={superRow.contextRef.current}
				isFullWidthRow={superRow.isFullWidthRow}
				fullWidthCellRenderer={CompetencySuperRowContent}
				fullWidthCellRendererParams={{ competencies }}
				getRowHeight={superRow.getRowHeight}
				defaultColDef={{
					cellStyle: { display: "flex", alignItems: "center" },
					resizable: false,
					suppressMovable: true,
					sortingOrder: ["asc", "desc"],
				}}
				emptyStateText="No Competencies Found"
				onSortChanged={onSortChanged}
				initialState={{
					sort: {
						sortModel: sort.name ? [{ colId: sort.name, sort: sort.direction }] : [],
					},
				}}
			/>
		</div>
	);
};

export default CompetenciesTable;
